import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// IT Services / Web Projects / Marketing & Advertising — Proposals & SOW Page
//
// Interactive Proposal & SOW Builder with milestone payment schedules
// (e.g., 30% Advance, 40% Mid-term, 30% Handover), deliverable sign-offs, SLA
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FileText, Plus, Trash2, Edit, Eye, Send, Check, X, ArrowRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import { formatINR } from '@/shared/utils/currency';
import { useDeals } from '@/features/deals/hooks/useDeals';
import { useAccountsCustomers } from '@/features/accounts/hooks/useParties';
import {
  useProposals,
  useProposalMutations,
  useActiveRateCard,
} from '../hooks/useServicesProjectsApi';

interface Milestone {
  key: string;
  sequence: number;
  name: string;
  description: string;
  deliverables: string[];
  percentage: number;
  amountMinor: number;
  dueDate: string;
}

interface Deliverable {
  key: string;
  name: string;
  description: string;
  acceptanceCriteria: string;
  milestoneId: string;
}

const emptyMilestone = (seq: number): Milestone => ({
  key: Math.random().toString(36).slice(2),
  sequence: seq,
  name: '',
  description: '',
  deliverables: [],
  percentage: 0,
  amountMinor: 0,
  dueDate: '',
});

const emptyDeliverable = (): Deliverable => ({
  key: Math.random().toString(36).slice(2),
  name: '',
  description: '',
  acceptanceCriteria: '',
  milestoneId: '',
});

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function ProposalsPage() {
  const [searchParams] = useSearchParams();
  const targetProposalId = searchParams.get('proposalId');

  const [creating, setCreating] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewProposalData, setViewProposalData] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    clientId: '',
    dealId: '',
    title: '',
    description: '',
    scope: '',
    rateCardId: '',
    totalValueMinor: 0,
    currency: 'INR',
    validUntil: '',
    milestones: [emptyMilestone(1)] as Milestone[],
    deliverables: [] as Deliverable[],
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'milestones' | 'deliverables'>('details');

  const { data: proposals = [], isLoading } = useProposals();
  const { data: dealsData } = useDeals({});
  const { data: customers = [] } = useAccountsCustomers();
  const { data: activeRateCard } = useActiveRateCard();
  const { create, update, send, accept, reject, convertToContract } = useProposalMutations();

  const clients = useMemo(() => customers, [customers]);
  const dealOptions = useMemo(() => (dealsData?.deals || []).map((d: any) => ({ value: d.id, label: `${d.title} (${d.business_name})` })), [dealsData]);

  useEffect(() => {
    if (targetProposalId && proposals.length > 0) {
      const match = proposals.find((p: any) => p._id === targetProposalId);
      if (match) {
        setViewProposalData(match);
      }
    }
  }, [targetProposalId, proposals]);

  const resetForm = useCallback(() => {
    setFormData({
      clientId: '',
      dealId: '',
      title: '',
      description: '',
      scope: '',
      rateCardId: activeRateCard?._id || '',
      totalValueMinor: 0,
      currency: 'INR',
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      milestones: [emptyMilestone(1)],
      deliverables: [],
    });
    setActiveTab('details');
  }, [activeRateCard]);

  const totalPercentage = useMemo(() =>
    formData.milestones.reduce((sum, m) => sum + (m.percentage || 0), 0), [formData.milestones]);

  const computedAmounts = useMemo(() => {
    const total = formData.totalValueMinor;
    return formData.milestones.map(m => ({
      ...m,
      amountMinor: Math.round((m.percentage || 0) / 100 * total),
    }));
  }, [formData.milestones, formData.totalValueMinor]);

  const setMilestone = (key: string, patch: Partial<Milestone>) =>
    setFormData(f => ({ ...f, milestones: f.milestones.map(m => m.key === key ? { ...m, ...patch } : m) }));

  const addMilestone = () =>
    setFormData(f => ({ ...f, milestones: [...f.milestones, emptyMilestone(f.milestones.length + 1)] }));

  const removeMilestone = (key: string) =>
    setFormData(f => ({ ...f, milestones: f.milestones.filter(m => m.key !== key).map((m, i) => ({ ...m, sequence: i + 1 })) }));

  const handleCreate = () => {
    resetForm();
    setCreating(true);
  };

  const handleView = (p: any) => {
    setViewProposalData(p);
  };

  const handleEdit = (p: any) => {
    setFormData({
      clientId: p.clientId || '',
      dealId: p.dealId || '',
      title: p.title,
      description: p.description || '',
      scope: p.scope || '',
      rateCardId: p.rateCardId || '',
      totalValueMinor: p.totalAmountMinor || 0,
      currency: p.currency || 'INR',
      validUntil: p.validUntil ? new Date(p.validUntil).toISOString().split('T')[0] : '',
      milestones: (p.milestones || []).map((m: any, i: number) => ({
        key: m._id || Math.random().toString(36).slice(2),
        sequence: i + 1,
        name: m.name,
        description: m.description || '',
        deliverables: m.deliverables || [],
        percentage: m.percentage || 0,
        amountMinor: m.amountMinor || 0,
        dueDate: m.dueDate ? new Date(m.dueDate).toISOString().split('T')[0] : '',
      })),
      deliverables: (p.deliverables || []).map((d: any) => ({
        key: d._id || Math.random().toString(36).slice(2),
        name: d.name,
        description: d.description || '',
        acceptanceCriteria: d.acceptanceCriteria || '',
        milestoneId: d.milestoneId || '',
      })),
    });
    setCreating(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId || !formData.title.trim()) {
      return toast.error('Client and Title are required');
    }
    if (totalPercentage !== 100) {
      return toast.error(`Milestone percentages must sum to 100% (currently ${totalPercentage}%)`);
    }

    setSaving(true);
    try {
      const payload: any = {
        ...formData,
        totalValueMinor: formData.totalValueMinor,
        validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : null,
        milestones: computedAmounts.map(({ key: _k, ...m }) => m),
        deliverables: formData.deliverables.map(({ key: _k, ...d }) => d),
      };

      if (formData.clientId) {
        const client = clients.find((c: any) => c._id === formData.clientId);
        payload.clientName = client?.name || '';
      }

      if (viewingId) {
        await update.mutateAsync({ id: viewingId, payload });
        toast.success('Proposal updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Proposal created');
      }
      setCreating(false);
      setViewingId(null);
    } catch (err) {
      toast.error(errorText(err, 'Failed to save proposal'));
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id: string) => {
    if (!window.confirm('Send this proposal to the client?')) return;
    try {
      await send.mutateAsync(id);
      toast.success('Proposal sent');
    } catch (err) {
      toast.error(errorText(err, 'Failed to send proposal'));
    }
  };

  const handleAccept = async (id: string) => {
    if (!window.confirm('Accept this proposal?')) return;
    try {
      await accept.mutateAsync(id);
      toast.success('Proposal accepted');
    } catch (err) {
      toast.error(errorText(err, 'Failed to accept proposal'));
    }
  };

  const handleReject = async (id: string) => {
    const reason = window.prompt('Rejection reason (required):');
    if (!reason?.trim()) return;
    try {
      await reject.mutateAsync({ id, reason: reason.trim() });
      toast.success('Proposal rejected');
    } catch (err) {
      toast.error(errorText(err, 'Failed to reject proposal'));
    }
  };

  const handleConvert = async (p: any) => {
    if (!window.confirm(`Convert proposal ${p.proposalNumber} to a contract?`)) return;
    try {
      await convertToContract.mutateAsync({ id: p._id, payload: {} });
      toast.success('Contract created from proposal');
    } catch (err) {
      toast.error(errorText(err, 'Failed to convert to contract'));
    }
  };

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={FileText}
        eyebrow="Projects & Services"
        title="Proposals & SOWs"
        description={<>Build proposals with milestone payment schedules, deliverables, and acceptance criteria.</>}
        actions={
          <><Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-1" /> New Proposal
                </Button></>
        }
      />

      {/* Active Rate Card Info */}
      {activeRateCard && (
        <div className="bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-800 rounded-lg p-3 text-sm">
          <span className="font-medium text-blue-800 dark:text-blue-200">Active Rate Card:</span>{' '}
          <span className="text-blue-600 dark:text-blue-400">{activeRateCard.code} — {activeRateCard.name}</span>
        </div>
      )}

      {/* Proposals List */}
      {proposals.length === 0 ? (
        <EmptyState
          title="No proposals yet"
          description="Create your first proposal with milestones, deliverables, and payment schedules."
          action={<Button onClick={handleCreate}>Create Proposal</Button>}
        />
      ) : (
        <div className="space-y-3">
          {proposals.map((p: any) => (
            <div key={p._id} className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-center gap-3">
              <FileText className="h-4 w-4 text-blue-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{p.proposalNumber || p._id.slice(-6)}</span>
                  <Badge className={p.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                    p.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                    p.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    p.status === 'converted' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'}>
                    {p.status}
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-800">v{p.version || 1}</Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {p.clientName || 'No client'} · {p.milestones?.length || 0} milestone(s) ·{' '}
                  {new Date(p.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-sm">{formatINR(p.totalAmountMinor || 0)}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => handleView(p)} title="View">
                  <Eye className="h-4 w-4" />
                </Button>
                {p.status === 'draft' && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => handleSend(p._id)}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Send
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(p)} title="Edit">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {p.status === 'sent' && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => handleAccept(p._id)}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Accept
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleReject(p._id)}>
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </>
                )}
                {p.status === 'accepted' && (
                  <Button variant="secondary" size="sm" onClick={() => handleConvert(p)}>
                    <ArrowRight className="h-3.5 w-3.5 mr-1" /> Convert to Contract
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal open={creating} onClose={() => { setCreating(false); setViewingId(null); }} title={viewingId ? 'Edit Proposal' : 'Create Proposal'} size="lg">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Tab Navigation */}
          <div className="flex border-b border-border px-4">
            {(['details', 'milestones', 'deliverables'] as const).map(tab => (
              <Button
                key={tab}
                type="button"
                variant={activeTab === tab ? 'secondary' : 'ghost'}
                size="sm"
                className="px-3 py-2"
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Client *</label>
                    <Select
                      value={formData.clientId}
                      placeholder="Select client"
                      options={clients.map((c: any) => ({ value: c._id || c.id, label: c.name || c.legalName || c.displayName || c._id }))}
                      onChange={e => setFormData({ ...formData, clientId: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Deal (Optional)</label>
                    <Select
                      value={formData.dealId}
                      placeholder="Link to deal"
                      options={dealOptions}
                      onChange={e => setFormData({ ...formData, dealId: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    placeholder="Title *"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                  <Select
                    value={formData.currency}
                    options={['INR', 'USD', 'EUR', 'GBP', 'AED'].map(c => ({ value: c, label: c }))}
                    onChange={e => setFormData({ ...formData, currency: e.target.value })}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="Total Value (minor units)"
                    value={formData.totalValueMinor}
                    onChange={e => setFormData({ ...formData, totalValueMinor: parseInt(e.target.value) || 0 })}
                  />
                  <Input
                    type="date"
                    placeholder="Valid Until"
                    value={formData.validUntil}
                    onChange={e => setFormData({ ...formData, validUntil: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Description</label>
                  <textarea
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100 min-h-[80px]"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the proposal"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Scope of Work</label>
                  <textarea
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100 min-h-[120px]"
                    value={formData.scope}
                    onChange={e => setFormData({ ...formData, scope: e.target.value })}
                    placeholder="Detailed scope of work, deliverables, timelines, assumptions..."
                  />
                </div>
              </div>
            )}

            {/* Milestones Tab */}
            {activeTab === 'milestones' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Total: </span>
                    <strong>{totalPercentage}%</strong>
                    {totalPercentage !== 100 && <span className="text-red-500 ml-2">(must equal 100%)</span>}
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={addMilestone}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Milestone
                  </Button>
                </div>

                <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        {['#', 'Name', 'Description', 'Deliverables', '%', 'Amount', 'Due Date', ''].map(h => (
                          <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {computedAmounts.map((m) => (
                        <tr key={m.key} className="bg-white dark:bg-slate-900">
                          <td className="px-3 py-2 w-8 text-center">{m.sequence}</td>
                          <td className="px-3 py-2 w-32">
                            <Input
                              placeholder="Milestone name"
                              value={m.name}
                              onChange={e => setMilestone(m.key, { name: e.target.value })}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              placeholder="Description"
                              value={m.description}
                              onChange={e => setMilestone(m.key, { description: e.target.value })}
                            />
                          </td>
                          <td className="px-3 py-2 w-40">
                            {m.deliverables.join(', ') || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2 w-16">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="%"
                              value={m.percentage}
                              onChange={e => setMilestone(m.key, { percentage: parseInt(e.target.value) || 0 })}
                            />
                          </td>
                          <td className="px-3 py-2 w-28 font-mono text-right">{formatINR(m.amountMinor)}</td>
                          <td className="px-3 py-2 w-28">
                            <Input
                              type="date"
                              value={m.dueDate}
                              onChange={e => setMilestone(m.key, { dueDate: e.target.value })}
                            />
                          </td>
                          <td className="px-3 py-2 w-10 text-right">
                            <Button variant="ghost" size="icon" onClick={() => removeMilestone(m.key)} disabled={formData.milestones.length <= 1}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Deliverables Tab */}
            {activeTab === 'deliverables' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-600">Deliverables</label>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setFormData(f => ({ ...f, deliverables: [...f.deliverables, emptyDeliverable()] }))}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Deliverable
                  </Button>
                </div>

                <div className="space-y-3">
                  {formData.deliverables.map((d, i) => (
                    <div key={d.key} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">Deliverable #{i + 1}</span>
                        <Button variant="ghost" size="icon" onClick={() => setFormData(f => ({ ...f, deliverables: f.deliverables.filter(x => x.key !== d.key) }))}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Input
                          placeholder="Name"
                          value={d.name}
                          onChange={e => setFormData(f => ({ ...f, deliverables: f.deliverables.map(x => x.key === d.key ? { ...x, name: e.target.value } : x) }))}
                        />
                        <Select
                          value={d.milestoneId}
                          placeholder="Link to milestone"
                          options={[{ value: '', label: 'No milestone' }, ...computedAmounts.map(m => ({ value: m.key, label: `${m.sequence}. ${m.name} (${m.percentage}%)` }))]}
                          onChange={e => setFormData(f => ({ ...f, deliverables: f.deliverables.map(x => x.key === d.key ? { ...x, milestoneId: e.target.value } : x) }))}
                        />
                      </div>
                      <Input
                        placeholder="Description"
                        value={d.description}
                        onChange={e => setFormData(f => ({ ...f, deliverables: f.deliverables.map(x => x.key === d.key ? { ...x, description: e.target.value } : x) }))}
                      />
                      <Input
                        placeholder="Acceptance Criteria"
                        value={d.acceptanceCriteria}
                        onChange={e => setFormData(f => ({ ...f, deliverables: f.deliverables.map(x => x.key === d.key ? { ...x, acceptanceCriteria: e.target.value } : x) }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3 bg-slate-50 dark:bg-slate-900">
            <Button type="button" variant="outline" onClick={() => { setCreating(false); setViewingId(null); }}>Cancel</Button>
            <Button type="submit" loading={saving}>
              {viewingId ? 'Update' : 'Create'} Proposal
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Proposal Detail Modal */}
      {viewProposalData && (
        <Modal open={!!viewProposalData} onClose={() => setViewProposalData(null)} title="Proposal Details" size="lg">
          <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
              <div>
                <h2 className="text-xl font-bold">{viewProposalData.title}</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Proposal #{viewProposalData.proposalNumber || viewProposalData._id?.slice(-6)} · Client: <strong className="text-slate-800 dark:text-slate-200">{viewProposalData.clientName || 'N/A'}</strong>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={
                  viewProposalData.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                  viewProposalData.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                  viewProposalData.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  viewProposalData.status === 'converted' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'
                }>
                  {viewProposalData.status}
                </Badge>
                <span className="text-lg font-bold font-mono text-emerald-600 dark:text-emerald-400">
                  {formatINR(viewProposalData.totalAmountMinor || viewProposalData.totalValueMinor || 0)}
                </span>
              </div>
            </div>

            {viewProposalData.description && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Description</h3>
                <p className="text-sm bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg text-slate-700 dark:text-slate-300">
                  {viewProposalData.description}
                </p>
              </div>
            )}

            {viewProposalData.scope && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Scope of Work</h3>
                <p className="text-sm bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {viewProposalData.scope}
                </p>
              </div>
            )}

            {/* Milestones Breakdown */}
            {viewProposalData.milestones && viewProposalData.milestones.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Milestone Payment Schedule</h3>
                <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 text-xs font-semibold text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">%</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-left">Due Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {viewProposalData.milestones.map((m: any, idx: number) => (
                        <tr key={m._id || idx} className="bg-white dark:bg-slate-900">
                          <td className="px-3 py-2 font-mono text-center">{m.sequence || idx + 1}</td>
                          <td className="px-3 py-2 font-medium">{m.name}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{m.description || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">{m.percentage}%</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(m.amountMinor)}</td>
                          <td className="px-3 py-2 text-xs">{m.dueDate ? new Date(m.dueDate).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Deliverables */}
            {viewProposalData.deliverables && viewProposalData.deliverables.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Deliverables</h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {viewProposalData.deliverables.map((d: any, idx: number) => (
                    <div key={d._id || idx} className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3 space-y-1">
                      <div className="font-semibold text-sm">{d.name}</div>
                      {d.description && <div className="text-xs text-muted-foreground">{d.description}</div>}
                      {d.acceptanceCriteria && (
                        <div className="text-xs text-slate-600 dark:text-slate-300 font-mono mt-1">
                          Acceptance: {d.acceptanceCriteria}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t border-border">
              <Button onClick={() => setViewProposalData(null)}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}